import styled from '@emotion/styled';
import {
  Button,
  Card,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel as _FormLabel,
  FormControl,
  CardActions as _CardActions,
  CardContent as _CardContent,
  CardMedia as _CardMedia,
  Drawer,
  Grid,
  Typography,
  IconButton as _IconButton,
  Tooltip,
} from '@material-ui/core';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { COLOR_TEXT_MAIN } from '../../theme/colors';
import AddIcon from '@material-ui/icons/Add';
import LinkIcon from '@material-ui/icons/Link';
import StyledInputField from '../component/primitive/StyledInputField';
import StyledFormLabel from '../component/primitive/StyledFormLabel';
import LoadingButton from '../component/primitive/LoadingButton';
import { Organization } from '@kaizenplatform/intelligence-entity';
import PagePreview from '../../entity/PagePreview';
import Loading from '../component/compound/Loading';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import useImageUrlPreview from '../../hooks/useImageUrlPreview';
import _PreviewImage from '../../view/component/compound/PreviewImage';
import { useDebounce } from '../../hooks/useDebounce';
import UploadButton from '../component/primitive/UploadButton';
import { useValidateUploadImage } from '../../hooks/useValidateUploadImage';
import bytesToSize from '../../utility/bytesToSize';
import {
  Content as DrawerContent,
  Container as DrawerContainer,
  Footer as DrawerFooter,
} from '../component/compound/Drawer';

type Props = {
  organization: Organization | null;
  pagePreviews: PagePreview[] | null;
  pagePreviewsLoading: boolean;
  createdPreviewId: string | null;
  createdPreviewIdLoading: boolean;
  createPagePreview: (
    args:
      | {
          content: string;
          contentOption: 'imageUrl';
          title: string;
          body: string;
          organizationId: string;
        }
      | {
          content: File;
          contentOption: 'upload';
          title: string;
          body: string;
          organizationId: string;
        },
  ) => Promise<void>;
  notifyUnimportant: (
    message: string,
    { messageValues, action, actionValues, isRaw, onClickAction, severity }?: any,
  ) => void;
  onMount: () => void;
  onUnmount?: () => void;
  className?: string;
};

//@see https://github.com/kaizenplatform/planbcd/blob/2fb0735123d42f012d2028bf76005711329fcb34/config/settings.yml#L27-L32
//2MB
const MAX_UPLOAD_SIZE = 2097152;

const PreviewCommentPage: FC<Props> = ({
  organization,
  pagePreviews,
  pagePreviewsLoading,
  createdPreviewId,
  createdPreviewIdLoading,
  // createdPreviewIdLoading,
  notifyUnimportant,
  createPagePreview,
  onMount,
  onUnmount,
  className,
}) => {
  useEffect(() => {
    if (organization) {
      onMount();
    }

    return () => {
      if (onUnmount) onUnmount();
    };
  }, [organization]);

  useEffect(() => {
    if (createdPreviewId) {
      closeDrawer();
      onMount();
      window.open(`/engine/comment-board/${createdPreviewId}`);
    }
  }, [createdPreviewId]);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [contentOption, setContentOption] = useState<'imageUrl' | 'upload' | 'none'>('imageUrl');
  const [, copyToClipBoard] = useCopyToClipboard();
  const {
    generatePreviewUrl,
    previewUrl,
    error: generatePreviewUrlError,
    generating,
    isValid: isValidImageUrl,
    reset: resetImageUrlPreview,
  } = useImageUrlPreview();
  const debounceImageUrl = useDebounce(imageUrl, 500);
  const {
    error: uploadImageError,
    file: uploadFile,
    onChange: onChangeUploadFile,
    previewUrl: uploadImagePreview,
    reset: resetUploadImage,
    isValid: isValidUploadImage,
  } = useValidateUploadImage(MAX_UPLOAD_SIZE);

  useEffect(() => {
    if (debounceImageUrl !== '') {
      generatePreviewUrl(imageUrl);
    } else {
      resetImageUrlPreview();
    }
  }, [debounceImageUrl]);

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTitle(event.target.value);
  };

  const handleBodyChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setBody(event.target.value);
  };

  const handleContentOptions = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContentOption(event.target.value as 'imageUrl' | 'upload' | 'none');
    resetImageUrlPreview();
    resetUploadImage();
    setImageUrl('');
  };

  const handleImageUrlChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setImageUrl(event.target.value);
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setOpen(open);
  };

  const createNewPagePreview = async () => {
    if (organization) {
      try {
        if (contentOption === 'imageUrl') {
          await createPagePreview({
            content: imageUrl,
            contentOption: 'imageUrl',
            title,
            body,
            organizationId: organization.id,
          });
        } else if (contentOption === 'upload') {
          if (uploadFile === null) {
            throw new Error('ファイルが選択されていません。');
          }
          if (typeof organization.id !== 'string') {
            throw new Error('組織IDが取得できませんでした。');
          }
          await createPagePreview({
            content: uploadFile,
            contentOption: 'upload',
            title,
            body,
            organizationId: organization.id,
          });
        } else {
          throw new Error('予期せぬコンテントです。');
        }
      } catch (e) {
        notifyUnimportant('ページの追加に失敗しました。', { severity: 'error' });
      }
    }
  };

  const onClickShareButton = (shareId: string) => {
    // window.openを使うと、子タブが親タブのメモリーを消費していて、閉じてもそのメモリーが解放されないバグがあるため、親タブがフリーズする
    // @see https://stackoverflow.com/questions/49705644/window-open-with-blank-consumes-parent-tab-memory
    let link = document.createElement('a');
    link.setAttribute('href', `/engine/comment-board/${shareId}`);
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener');
    link.click();
  };

  const closeDrawer = () => {
    setTitle('');
    setBody('');
    setImageUrl('');
    resetUploadImage();
    setOpen(false);
  };

  const handleLinkCopy = (e: React.MouseEvent<unknown>, pagePreviewId: string) => {
    e.stopPropagation();
    copyToClipBoard(`${window.location.href.replace(location.search, '')}/${pagePreviewId}`)
      .then((result) => {
        if (result) {
          notifyUnimportant('リンクをコピーしました', { severity: 'success' });
        } else {
          notifyUnimportant('リンクのコピーに失敗しました', { severity: 'error' });
        }
      })
      .catch(() => {
        notifyUnimportant('リンクのコピーに失敗しました', { severity: 'error' });
      });
  };

  const canSubmitImageUrl = !!title && !!imageUrl && isValidImageUrl && !generating;
  const canSubmitUploadImage = !!title && isValidUploadImage;

  const canSubmit =
    contentOption === 'imageUrl' ? canSubmitImageUrl : contentOption === 'upload' ? canSubmitUploadImage : true;

  return (
    <Root className={className}>
      <Drawer
        anchor="right"
        open={open}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            toggleDrawer(false);
          }
        }}
      >
        <DrawerContainer>
          <DrawerContent>
            <Header>
              <Title>新規作成</Title>
            </Header>
            <Content>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <StyledInputField value={title} text="タイトル" onChange={handleTitleChange} />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {/* メモは任意項目 */}
                  <StyledInputField value={body} text="メモ" onChange={handleBodyChange} required={false} />
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <StyledFormLabel required>ファイルの登録</StyledFormLabel>
                    <RadioGroup
                      row
                      aria-label="contentOptions"
                      name="row-radio-buttons-group"
                      onChange={handleContentOptions}
                    >
                      <FormControlLabel
                        value="imageUrl"
                        control={<Radio disableRipple />}
                        label="画像URL"
                        checked={contentOption === 'imageUrl'}
                      />
                      <FormControlLabel
                        value="upload"
                        control={<Radio disableRipple />}
                        label="アップロード"
                        checked={contentOption === 'upload'}
                      />
                    </RadioGroup>

                    {contentOption === 'imageUrl' && (
                      <StyledInputField
                        value={imageUrl}
                        onChange={handleImageUrlChange}
                        placeholder="https://example.com/example.jpg"
                        helperText={<span>{generatePreviewUrlError}</span>}
                      />
                    )}
                    {contentOption === 'upload' && (
                      <div>
                        <UploadButton onChange={onChangeUploadFile} name="image">
                          ファイルを選択してください
                        </UploadButton>
                        <span>最大{bytesToSize(MAX_UPLOAD_SIZE)}</span>
                        {uploadFile !== null && <UploadImageName>{uploadFile.name}</UploadImageName>}
                        {uploadImageError && <UploadImageErrorText>{uploadImageError}</UploadImageErrorText>}
                      </div>
                    )}
                  </FormControl>
                  {(previewUrl || uploadImagePreview) && (
                    <PreviewImage url={previewUrl || uploadImagePreview} loading={generating} />
                  )}
                </Grid>
              </Grid>
            </Content>
          </DrawerContent>
          <DrawerFooter>
            <CancelButton onClick={closeDrawer}>キャンセル</CancelButton>
            <StyledButton disabled={!canSubmit} onClick={createNewPagePreview} loading={createdPreviewIdLoading}>
              新規作成する
            </StyledButton>
          </DrawerFooter>
        </DrawerContainer>
      </Drawer>
      <Container>
        <Header>
          <Title>コメント機能</Title>
          <StyledButton startIcon={<AddIcon />} onClick={toggleDrawer(true)}>
            新規作成
          </StyledButton>
        </Header>
        <Content>
          <Grid container spacing={3}>
            {!pagePreviewsLoading ? (
              pagePreviews ? (
                pagePreviews.map((pagePreview) => {
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={pagePreview.id}>
                      <CommentCard
                        onClick={() => {
                          onClickShareButton(pagePreview.id);
                        }}
                      >
                        <CardMedia
                          style={{
                            height: 200,
                            backgroundPosition: 'top',
                          }}
                          image={pagePreview.imageUrl}
                        />
                        <CardContent>
                          <CardTitle title={pagePreview.title}>{pagePreview.title}</CardTitle>
                          <CardActions disableSpacing>
                            <UpdatedAt></UpdatedAt>
                            <div />
                            <Tooltip title={<TooltipTitle>リンクをコピーする</TooltipTitle>} placement="bottom">
                              <IconButton onClick={(e) => handleLinkCopy(e, pagePreview.id)} aria-label="copy link">
                                <LinkIcon />
                              </IconButton>
                            </Tooltip>
                          </CardActions>
                        </CardContent>
                      </CommentCard>
                    </Grid>
                  );
                })
              ) : null
            ) : (
              <Loading />
            )}
          </Grid>
        </Content>
      </Container>
    </Root>
  );
};

const Root = styled('div')({});

const Container = styled('div')(`
  padding: 36px;
`);

const Header = styled('div')(`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 36px;
`);

const Content = styled('div')(`
  height: 100%;
  width: 100%;
`);

const Title = styled(Typography)(`
  font-weight: 700;
  font-size: 24px;
  color: ${COLOR_TEXT_MAIN};
`);

const CardTitle = styled(Typography)(`
  font-weight: 700;
  font-size: 16px;
  color: ${COLOR_TEXT_MAIN};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 10px;
`);

const TooltipTitle = styled(Typography)(`
  font-size: 12px;
  color: #FFFFFF;
`);

const UpdatedAt = styled(Typography)(`
  font-size: 12px;
  color: ${COLOR_TEXT_MAIN}
`);

const CardContent = styled(_CardContent)(`
`);

const CardMedia = styled(_CardMedia)(`
`);

const CommentCard = styled(Card)(`
  :hover{
    cursor: pointer;
  }
`);

const StyledButton = styled(LoadingButton)(`
  color: #FFFFFF;
  width: 128px;
  height: 32px;
  font-size: 14px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 800;
  border-color: ${COLOR_TEXT_MAIN};
  border-radius: 4px;
  :hover {
    background: #707889;
    border-color: #707889;
  }
  :disabled {
    background: #E6E7E8;
    color: rgba(75, 85, 106, 0.24);
    border: rgba(75, 85, 106, 0.24);
  }
`);

const CancelButton = styled(Button)(`
  color: ${COLOR_TEXT_MAIN};
  width: 128px;
  height: 32px;
  font-size: 14px;
  background: #FFFFFF;
  font-weight: 800;
  border: 1.5px solid #E6E7E8;
  border-radius: 4px;
  margin-right: 16px;
  :hover {
    background: #FFFFFF;
  }
`);

const IconButton = styled(_IconButton)(`
  width: 24px;
  height: 24px;
`);

const CardActions = styled(_CardActions)(`
  padding:0;
  display:grid;
  grid-template-columns: auto 34px 34px;
  gap: 10px;
  align-items: center;
`);

const PreviewImage = styled(_PreviewImage)(`
height: 300px;
`);

const UploadImageErrorText = styled(Typography)(`
  font-size: 12;
  margin: 12px 0px;
  color: ${COLOR_TEXT_MAIN};
`);
const UploadImageName = styled(Typography)(`
  font-size: 12;
  margin: 12px 0px;
  color: ${COLOR_TEXT_MAIN};
  text-overflow: ellipsis;
  overflow: hidden;
white-space: nowrap;
`);

export default PreviewCommentPage;
